//Common
import {
  ORIGEM_LOGIN,
  PATH_FERRAMENTA_ADM_SYSTEM,
  STATUS_CONSULTA_CNPJ,
} from "@/common/constantes";
import { fn_loadWebservicesCnpj } from "@/common/utils/cnpj";
import { fn_getDataAtual } from "@/common/utils/date";
import {
  fn_removerAcentuacao,
  fn_removerFormatacao,
  fn_toBoolean,
  fn_url_codificarValor,
  fn_validarCep,
  fn_validarCnpj,
} from "@/common/utils/util";

//Util
import {
  showMensagemErroApi,
  MENSAGENS_ERRO,
  MENSAGENS_INFO,
  showMensagemErro,
  showMensagemInfo,
  showMensagemAviso,
  showMensagemSucesso,
} from "@/frontend/utils/mensagens";

//API
import api from "./api";

/* OBS.: ESSAS FUNÇÕES NÃO ENVIAM MENSAGEM DE ERRO SE O CODIGO DO ERRO FOR "401" - SESSÃO EXPIRADA
 *       POIS A SESSÃO EXPIRADA É TRATADA NO ARQUIVO API.JS, NA CONFIGURAÇÃO DO AXIOS,
 *       REDIRECIONANDO PARA UMA PÁGINA DE SESSÃO EXPIRDA
 */

/*
 * Envia os dados do login para validações e retorno do acesso
 */
export async function api_login(identificacao, senha, path) {
  let ret;
  try {
    const { data } = await api.post("/login", {
      identificacao,
      senha,
      path,
      origem:
        path == PATH_FERRAMENTA_ADM_SYSTEM
          ? ORIGEM_LOGIN.SIIG_ADM_SYSTEM
          : ORIGEM_LOGIN.SIIG_WEB,
    });

    if (data) {
      ret = data;
    }
  } catch (error) {
    showMensagemErroApi(error, "logar");
  }
  return ret;
}

/*
 * Avisa o servidor da saída do sistema, para o usuário sair da consulta de usuários conectados.
 * A saída da tela não depende desta chamada: não mostra mensagem e não trata a sessão (sessão expirada,
 * manutenção). O token vem como parâmetro, pois a sessão do navegador é apagada logo em seguida.
 */
export async function api_logout(token) {
  try {
    await api.post(
      "/logout",
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
        ignorarTratamentoSessao: true,
      },
    );
  } catch {
    //A saída do sistema não depende do servidor
  }
}

/*
 * Obtém o logo do tenant gravado no banco
 */
export async function api_getLogoTenant(path) {
  const response = await api.get(`/tenant/getLogo?path=${path}`, {
    responseType: "blob",
  });

  let img = null;
  if (response.data?.size > 0) {
    img = URL.createObjectURL(response.data);
  }

  return img;
}

/*
 * Verifica o status do serviço de job
 */
export async function api_verificarStatusServicoJob() {
  let ret;
  try {
    const { data } = await api.get(`job/verificarStatusServico`);

    if (data) {
      ret = data;
    }
  } catch (error) {
    showMensagemErroApi(error, "consultar status serviço job");
  }
  return ret;
}

/*
 * Executa o job manualmente (monitor de jobs: usuário system e admin)
 * Retorna true se o job foi executado com sucesso
 */
export async function api_executarJobManual(idJob) {
  let ret = false;
  try {
    const { data } = await api.post(`job/executarJobManual/${idJob}`);

    ret = data === true;

    if (ret) {
      showMensagemSucesso("Job executado com sucesso.");
    } else {
      showMensagemAviso(
        "O job não foi executado com sucesso (pode estar em execução). Consulte o log do job.",
      );
    }
  } catch (error) {
    showMensagemErroApi(error, "executar o job");
  }
  return ret;
}

/*
 * Realiza o upload do arquivo
 */
export async function api_upload(pastaTenantSolicitacao, nomeArquivo, file) {
  let ret;
  try {
    const formData = new FormData();
    formData.append("file", file); // "file" deve ser o mesmo nome do multer.single("file")

    const data = await api.post("/upload", formData, {
      params: {
        pastaArquivo: "uploads/" + pastaTenantSolicitacao,
        nomeArquivo: nomeArquivo,
      },
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (data.data?.sucesso != true) {
      console.error("Upload ", nomeArquivo, " não foi realizado!");
      throw new Error("Upload não foi realizado.");
    }

    ret = data;
  } catch (error) {
    showMensagemErroApi(error, "realizar o upload");
  }
  return ret;
}

/*
 * Envia o arquivo PDF da nota (DANFE ou NFS-e) para leitura e retorna os dados extraídos
 * @comParcelas: (opcional) true na leitura da inclusão da solicitação: retorna também as parcelas do documento
 */
export async function api_extrairDadosNota(
  file,
  idSolicitacao,
  idSolicitacaoPag,
  comParcelas = false,
  idsConta = [], //contas para a sugestão das contas (só na inclusão da solicitação, com as parcelas)
) {
  let ret;
  try {
    const formData = new FormData();
    formData.append("file", file); // "file" deve ser o mesmo nome do UploadMemory.single("file")

    //Solicitação e pagamento da leitura, para o registro da leitura
    if (idSolicitacao) formData.append("id_solicitacao", idSolicitacao);
    if (idSolicitacaoPag)
      formData.append("id_solicitacao_pag", idSolicitacaoPag);
    if (comParcelas) formData.append("com_parcelas", "S");
    if (comParcelas && idsConta.length > 0) {
      formData.append("ids_conta", idsConta.join(","));
    }

    const { data } = await api.post("/nota_leitura/extrair", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (data) {
      ret = data;
    }
  } catch (error) {
    showMensagemErroApi(error, "ler os dados da nota");
  }
  return ret;
}

/*
 * Envia o arquivo do contrato (PDF ou imagem) para leitura e retorna os valores dos campos do contrato
 * @param campos: Campos do contrato apresentados na tela, com as opções já carregadas
 * @param idSolicitacao: Solicitação já gravada (para o registro da leitura). Nulo no cadastro de solicitação nova
 */
export async function api_extrairDadosContrato(file, campos, idSolicitacao) {
  let ret;
  try {
    const formData = new FormData();
    formData.append("file", file); // "file" deve ser o mesmo nome do UploadMemory.single("file")
    formData.append("campos", JSON.stringify(campos));

    //Solicitação da leitura, para o registro da leitura
    if (idSolicitacao) formData.append("id_solicitacao", idSolicitacao);

    const { data } = await api.post("/contrato_leitura/extrair", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (data) {
      ret = data;
    }
  } catch (error) {
    showMensagemErroApi(error, "ler os dados do contrato");
  }
  return ret;
}

/*
 * Grava no registro da leitura de documento por IA os campos que o usuário copiou para a tela.
 * OBS: Não apresenta mensagem de erro: a falha nesse registro não pode atrapalhar a cópia dos campos.
 * @param idLeitura: Id do registro da leitura (retornado pela leitura)
 * @param camposCopiados: Nomes dos campos copiados
 */
export async function api_registrarCopiaLeituraIA(idLeitura, camposCopiados) {
  if (!idLeitura) return;

  try {
    await api.put(`/log_leitura_documento_ia/registrarCopia/${idLeitura}`, {
      campos_copiados: camposCopiados,
    });
  } catch (error) {
    console.log("Erro api_registrarCopiaLeituraIA: ", error);
  }
}

/*
 * Faz o download de arquivo
 */
export async function api_download(
  pastaRelativa,
  nomeArquivo,
  preview = "N",
  showMessage = true,
) {
  try {
    const response = await api.get(`/download/${nomeArquivo}`, {
      params: { pasta_arquivo: pastaRelativa, preview: preview },
      responseType: "blob",
    });

    if (preview == "S") {
      return URL.createObjectURL(response.data);
    } else {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", nomeArquivo);

      document.body.appendChild(link);
      link.click();

      //document.body.removeChild(link);
      if (link.parentNode == document.body) {
        document.body.removeChild(link);
      }

      if (showMessage) {
        showMensagemInfo(MENSAGENS_INFO.DOWNLOAD_CONCLUIDO);
      }
    }
  } catch (error) {
    showMensagemErroApi(error, "tentar fazer o download");
  }
}

/*
 * Obtém o valor total do pagamento por situação dentro de determinado mês/ano vencimento
 */
export async function api_sumValorPagamentoPorSituacao(filtros) {
  let ret;

  try {
    const { data } = await api.get(
      `solicitacao_pag/sumValorPagamentoPorSituacao?${filtros}`,
    );

    if (data) {
      ret = data;
    }
  } catch (error) {
    showMensagemErroApi(error, "consultar total pagamento por situação");
  }
  return ret;
}

/*
 * Consulta o cep via webservice https://viacep.com.br/ws/${cep}/json
 * e retorna os dados do endereço do cep.
 * @param cep: Número do cep sem formatação
 */
export async function api_consultarCEP(cep) {
  let dados = null;

  //Valida o formato do CEP.
  if (fn_validarCep(cep)) {
    await fetch(`https://viacep.com.br/ws/${cep}/json/`)
      .then((response) => response.json())
      .then((data) => {
        if (!data?.erro || fn_toBoolean(data.erro) === false) {
          dados = {
            logradouro: data.logradouro
              ? fn_removerAcentuacao(data.logradouro.toUpperCase())
              : "",

            complemento: data.complemento
              ? fn_removerAcentuacao(data.complemento.toUpperCase())
              : " ",

            nome_bairro: data.bairro
              ? fn_removerAcentuacao(data.bairro.toUpperCase())
              : "",

            nome_cidade: data.localidade
              ? fn_removerAcentuacao(data.localidade.toUpperCase())
              : "",

            codigo_ibge_cidade: data.ibge,

            sigla_uf: data.uf,
          };
        }
      })
      .catch((error) => {
        console.error("Erro consulta cep: ", error);

        showMensagemErro(
          MENSAGENS_ERRO.TENTATIVA_OPERACAO,
          `consultar CEP: ${error.message}`,
        );
      });
  } else {
    showMensagemErro(MENSAGENS_ERRO.INVALIDO, "CEP");
  }

  if (!dados) {
    showMensagemErro(MENSAGENS_ERRO.NAO_ENCONTRADO, "CEP");
  }
  return dados;
}

/*
 * Consulta o cnpj e retorna os dados da empresa
 * A consulta é feita pelo servidor do SIIG (rota /pessoa_juridica/consultarCnpj), para não depender
 * do antivírus / firewall da máquina do usuário. Se o servidor não conseguir consultar,
 * a consulta é feita direto pelo navegador, como era antes
 * Retorna os dados, null (CNPJ inválido ou não encontrado) ou -1 (não conseguiu consultar)
 * @param cnpj: Número do cnpj com ou sem formatação
 */
export async function api_consultarPessoaJuridica(cnpj, showAviso = false) {
  // Valida o formato do CNPJ
  if (!fn_validarCnpj(cnpj)) {
    showMensagemErro(MENSAGENS_ERRO.INVALIDO, "CNPJ");
    return null;
  }

  const numeroCnpj = fn_removerFormatacao(cnpj);

  //Consulta pelo servidor do SIIG
  let ret = await api_consultarCnpjServidor(numeroCnpj);

  //Consulta direto pelo navegador (forma anterior), usada somente se o servidor não conseguir consultar
  //OBS: Para voltar a consultar só pelo navegador, comentar a consulta pelo servidor acima
  //     e deixar: let ret = await api_consultarCnpjNavegador(numeroCnpj);
  if (ret.status == STATUS_CONSULTA_CNPJ.FALHA) {
    ret = await api_consultarCnpjNavegador(numeroCnpj);
  }

  if (ret.status == STATUS_CONSULTA_CNPJ.OK) {
    const dados = {
      razao_social: fn_removerAcentuacao(
        ret.dados?.razao_social || "",
      ).toUpperCase(),

      nome_fantasia: fn_removerAcentuacao(
        ret.dados?.nome_fantasia || "",
      ).toUpperCase(),

      cep: ret.dados?.cep,
      numero: fn_removerAcentuacao(ret.dados?.numero || "").toUpperCase(),
      complemento: fn_removerAcentuacao(
        ret.dados?.complemento || "",
      ).toUpperCase(),
    };

    return dados;
  }

  if (ret.status == STATUS_CONSULTA_CNPJ.NAO_ENCONTRADO) {
    return null; //significa que a consulta não encontrou o CNPJ
  }

  //Nenhum webservice retornou os dados
  if (showAviso) {
    showMensagemAviso("Não foi possível obter os dados do CNPJ");
  }

  //-1 significa que não conseguiu consultar (pode ter estourado o limite de consulta)
  return ret.erroConexao ? -1 : null;
}

/*
 * Consulta o cnpj pelo servidor do SIIG, que consulta os webservices
 * Retorna o status da consulta (STATUS_CONSULTA_CNPJ) e, quando encontra, os dados da empresa
 * @param numeroCnpj: Número do cnpj sem formatação
 */
async function api_consultarCnpjServidor(numeroCnpj) {
  try {
    const { data } = await api.get(
      `/pessoa_juridica/consultarCnpj?cnpj=${numeroCnpj}`,
    );

    if (data?.status) {
      return data;
    }
  } catch (error) {
    console.error(error);
  }

  return { status: STATUS_CONSULTA_CNPJ.FALHA };
}

/*
 * Consulta o cnpj direto pelo navegador nos webservices, passando para o seguinte quando o anterior falha
 * Retorna o status da consulta (STATUS_CONSULTA_CNPJ) e, quando encontra, os dados da empresa
 * OBS: O antivírus / firewall da máquina do usuário pode bloquear o acesso a algum webservice
 * @param numeroCnpj: Número do cnpj sem formatação
 */
async function api_consultarCnpjNavegador(numeroCnpj) {
  let erroConexao = false;

  for (const webservice of fn_loadWebservicesCnpj(numeroCnpj)) {
    try {
      const response = await fetch(webservice.url);

      if (response.ok) {
        const data = await response.json();

        return {
          status: STATUS_CONSULTA_CNPJ.OK,
          dados: webservice.fn_extrairDados(data),
        };
      }

      if (response.status == 404) {
        return { status: STATUS_CONSULTA_CNPJ.NAO_ENCONTRADO };
      }

      //Webservice respondeu com erro (fora do ar, limite de consulta): tenta o próximo
      erroConexao = false;
    } catch (error) {
      //Não conseguiu acessar o webservice (sem conexão, bloqueio): tenta o próximo
      console.error(error);
      erroConexao = true;
    }
  }

  return { status: STATUS_CONSULTA_CNPJ.FALHA, erroConexao };
}

/*
 * Carrega os dados para o rateio automático por qtde colaborador e/ou por fat. filial, conforme o mês/ano da emissão
 */
export async function api_loadInfRateioAutomatico(
  dataEmissao,
  selectedCentros,
  formaRateio,
) {
  let ret;

  try {
    let filtros = `data_emissao=${fn_url_codificarValor(dataEmissao || fn_getDataAtual())}`;

    if (selectedCentros && selectedCentros.length > 1) {
      const listIdCentro = selectedCentros.map((item) => item.value).join(",");
      filtros += `&id_centro_custo=${listIdCentro}`;
    }

    //Se não informar a forma do rateio, vai consultar as duas formas: por fat. e por qtde colab
    if (formaRateio) {
      filtros += `&forma_rateio=${formaRateio}`;
    }

    const { data } = await api.get(
      `solicitacao_pag_rateio/loadInfRateioAutomatico?${filtros}`,
    );

    if (data) {
      ret = data;
    }
  } catch (error) {
    showMensagemErroApi(error, "carregar informação rateio automático");
  }
  return ret;
}

/*
 * Compara o rateio automático gravado do pagamento com o rateio atualizado (o que a posse gravaria)
 * @dataEmissao: emissão da tela da posse; sem ela, o backend usa a emissão gravada
 */
export async function api_compararRateioAtualizado(idPagamento, dataEmissao) {
  let ret = null;

  try {
    const filtros = dataEmissao
      ? `?data_emissao=${fn_url_codificarValor(dataEmissao)}`
      : "";

    const { data } = await api.get(
      `solicitacao_pag/compararRateioAtualizado/${idPagamento}${filtros}`,
    );

    if (data) {
      ret = data;
    }
  } catch (error) {
    showMensagemErroApi(error, "comparar rateio gravado com o atualizado");
  }
  return ret;
}

/*
 * Retorna a quantidade de notificação não lida do usuário
 */
export async function api_getCountNotificacaoNaoLida(idUsuario) {
  let count = null;
  try {
    const { data } = await api.get(
      `/notificacao_usuario/countNaoLido?id_usuario=${idUsuario}`,
    );

    count = data?.count;
  } catch (error) {
    showMensagemErroApi(error, "consultar qtde notificação não lida");
  }
  return count;
}

// /*
//  * Retorna as informações para o card de pendente autorização do Dashboard
//  */
// export async function api_getCountSolicitacao(dataInicio, dataFim, filtros) {
//   let ret;

//   try {
//     if (dataInicio) {
//       filtros += `&data_inicio=${fn_url_codificarValor(dataInicio)}`;
//     }

//     if (dataFim) {
//       filtros += `&data_fim=${fn_url_codificarValor(dataFim)}`;
//     }

//     const { data } = await api.get(`dashboard/getCountSolicitacao?${filtros}`);

//     if (data) {
//       ret = data;
//     }
//   } catch (error) {
//     showMensagemErroApi(error, "consultar contagem solicitação");
//   }
//   return ret;
// }

/*
 * Retorna as informações para o card de pendente pagamento do Dashboard
 */
export async function api_getCountPagamento(dataInicio, dataFim, filtros) {
  let ret;

  try {
    if (dataInicio) {
      filtros += `&data_vencimento_inicio=${fn_url_codificarValor(dataInicio)}`;
    }

    if (dataFim) {
      filtros += `&data_vencimento_fim=${fn_url_codificarValor(dataFim)}`;
    }

    const { data } = await api.get(`dashboard/getCountPagamento?${filtros}`);

    if (data) {
      ret = data;
    }
  } catch (error) {
    showMensagemErroApi(error, "consultar contagem pagamento");
  }
  return ret;
}

/*
 * Retorna o último log de autorização de determinado id do model
 */
export async function api_getUltimoLogAutorizacao(
  idAutorizacao,
  model,
  idModel,
) {
  let ret;

  try {
    const filtros = `id_autorizacao=${idAutorizacao}&model=${model}&id_model=${idModel}`;

    const { data } = await api.get(
      `autorizacao_log/getUltimoLogAutorizacao?${filtros}`,
    );

    if (data) {
      ret = data;
    }
  } catch (error) {
    showMensagemErroApi(error, "consultar log autorização");
  }
  return ret;
}

//Registro log do frontend
export function api_registrarLogErroFrontend(dados) {
  try {
    return api.post("/log_frontend/registrar", dados);
  } catch (error) {
    console.error("Erro api_logErroFrontend: ", error);
  }
}
